import { useEffect, useId, useMemo, useRef } from 'react';
import {
  Separator,
  Slider,
  Switch,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { COPY_GOALS_MAX_LENGTH } from '@inference.engine/widgets/web';
import { ChatOptionsProps } from '../ChatOptions.types';
import {
  SUPPORTED_CHANNELS,
  SUPPORTED_RESPONSE_STYLE,
  SUPPORTED_VARIATIONS,
} from './CopyGenerationOptions.types';
import type { ResponseStyle, Variations } from './CopyGenerationOptions.types';
import { getDefaultCopyOptions, toStoreCopyOptions } from './copy-options';
import { useOptionsManager } from '../hooks/useOptionsManager';
import { DEFAULT_SELECTABLE_MODEL, useAgentModelOptions } from '../hooks/useAgentModelOptions';
import { CopyChannelMultiSelect } from './CopyChannelMultiSelect';
import { useTranslation } from 'react-i18next';

const VARIATIONS_MIN = SUPPORTED_VARIATIONS[0].value;
const VARIATIONS_MAX = SUPPORTED_VARIATIONS[SUPPORTED_VARIATIONS.length - 1].value;

export const CopyGenerationOptions = ({ onOptionUpdated }: ChatOptionsProps) => {
  const { t } = useTranslation('chats');

  const filteredModels = useAgentModelOptions();
  const hasModels = filteredModels.length > 0;

  const defaultOptions = useMemo(() => {
    const firstModel = filteredModels[0]?.value ?? DEFAULT_SELECTABLE_MODEL;
    return getDefaultCopyOptions({ model: firstModel });
  }, [filteredModels]);

  const { options, setOptions } = useOptionsManager({
    defaultOptions,
    onOptionUpdated,
    toStore: toStoreCopyOptions,
  });

  const id = useId();
  const channelId = `${id}-channel`;
  const goalId = `${id}-goal`;
  const responseStyleId = `${id}-response-style`;
  const variationsId = `${id}-variations`;
  const runQaId = `${id}-run-qa`;

  const sliderRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    // Tessra's Slider forwards nothing to its thumb, which is the element carrying role="slider".
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule flags every ParentNode.querySelector call here, whatever the type argument
    const thumb = sliderRef.current?.querySelector('[role="slider"]');
    thumb?.setAttribute('aria-labelledby', variationsId);
  }, [variationsId]);

  return (
    <div className="flex flex-col gap-4 w-80">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('chats.copyGenerationOptions.copyAgentSettings')}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span id={channelId} className="text-xs font-medium text-foreground-subtle">
          {t('chats.copyGenerationOptions.channel')}
        </span>
        <CopyChannelMultiSelect
          aria-labelledby={channelId}
          value={options.channels}
          onChange={(channels) => setOptions({ channels })}
          options={SUPPORTED_CHANNELS}
          disabled={!hasModels}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span id={goalId} className="text-xs font-medium text-foreground-subtle">
          {t('chats.copyGenerationOptions.goal')}
        </span>
        <Textarea
          aria-labelledby={goalId}
          placeholder={t('chats.copyGenerationOptions.describeYourGoals')}
          value={options.goals ?? ''}
          onChange={(event) => setOptions({ goals: event.target.value })}
          maxLength={COPY_GOALS_MAX_LENGTH}
          disabled={!hasModels}
          className="min-h-24 border-0 bg-surface resize-none"
        />
      </div>

      <div className="flex flex-col gap-2">
        <span id={responseStyleId} className="text-xs font-medium text-foreground-subtle">
          {t('chats.copyGenerationOptions.responseStyle')}
        </span>
        <ToggleGroup
          aria-labelledby={responseStyleId}
          type="single"
          value={options.responseStyle}
          onValueChange={(value) => {
            if (value) setOptions({ responseStyle: value as ResponseStyle });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-3 w-full"
          disabled={!hasModels}
        >
          {SUPPORTED_RESPONSE_STYLE.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value} className="py-2">
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-2">
        <span id={variationsId} className="text-xs font-medium text-foreground-subtle">
          {t('chats.copyGenerationOptions.variations')}
        </span>
        <Slider
          ref={sliderRef}
          value={[options.variations]}
          min={VARIATIONS_MIN}
          max={VARIATIONS_MAX}
          step={1}
          onValueChange={([value]) => {
            setOptions({ variations: value as Variations });
          }}
          disabled={!hasModels}
          className="[&_[data-slot=slider-range]]:bg-foreground [&_[data-slot=slider-thumb]]:border-foreground"
        />
        <div className="flex justify-between text-xs">
          {SUPPORTED_VARIATIONS.map((option) => (
            <span
              key={option.value}
              className={
                option.value === options.variations
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground'
              }
            >
              {option.value}
            </span>
          ))}
        </div>
      </div>

      <Separator />

      <div className="flex items-center justify-between">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span id={runQaId} className="text-sm text-foreground cursor-help">
                {t('chats.copyGenerationOptions.runQa')}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="w-64 max-w-64">
              {t('chats.copyGenerationOptions.automaticallyEvaluateAndRefineCopy')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Switch
          aria-labelledby={runQaId}
          checked={options.enableQAFeedback}
          onCheckedChange={(checked) => setOptions({ enableQAFeedback: checked })}
          disabled={!hasModels}
          className="data-[state=checked]:bg-foreground"
        />
      </div>
    </div>
  );
};
